/**
 * ConfirmationModal.jsx — mobile fix (option 1b)
 *
 * What changed and why:
 *  1. `h-[100dvh] sm:max-h-[90vh]` instead of `max-h-[90vh]`.
 *     iOS measures `vh` against the LARGE viewport (URL bar hidden), so 90vh was
 *     taller than the visible area and the footer landed under Safari's toolbar.
 *     `dvh` tracks the *dynamic* viewport, so the sheet is always exactly as tall
 *     as what you can see.
 *  2. The scroll container is now the BODY only. Header and footer are flex
 *     children that never scroll, so the buttons are always on screen.
 *  3. Footer gets `pb-[calc(0.75rem+env(safe-area-inset-bottom))]` so the buttons
 *     clear the home-bar area.
 *  4. Buttons: `min-h-[52px]` on mobile / `min-h-[44px]` desktop, Confirm is
 *     `flex-2` so it's the wider target.
 *  5. Mobile is a full-screen sheet (no rounded corners, no margins); at `sm:`
 *     and up it goes back to the centered card it is today.
 *  6. Field rows are label/value pairs on mobile and fall back to the existing
 *     grids at `sm:`.
 *  7. Scroll cue: a pill above the footer that hides once you reach the bottom.
 *  8. Body scroll-lock while open.
 *
 * Tailwind note: `flex-2` isn't a default utility — use `basis-0 grow-[2]` (below)
 * or add `flex-2: '2 2 0%'` to theme.extend.flex.
 */

import React from 'react';

const ConfirmationModal = ({ isOpen, title, defaultValues: regValues, onConfirm, onGoBack, showParkingZone = false, submitting = false }) => {
  const [vClass, setVClass] = React.useState([{}]);
  const [parkingZones, setParkingZones] = React.useState([{}]);
  const [atBottom, setAtBottom] = React.useState(false);
  const scrollRef = React.useRef(null);

  // --- unchanged data fetching -------------------------------------------------
  const currentDomain = () =>
    location.origin.startsWith('http://localhost') ? 'http://localhost:3001' : location.origin;

  const classUUIDToName = async (uuid) => {
    const response = await fetch(`${currentDomain()}/api/class/${uuid}`);
    return response.json();
  };

  const parkingZoneIdToName = async (zoneId) => {
    const response = await fetch(`${currentDomain()}/api/parking`);
    const data = await response.json();
    const zone = data.find((z) => z.order === zoneId);
    return zone ? { name: zone.name, hexcolor: zone.hexcolor } : null;
  };

  const fetchClassName = async (uuid, index) => {
    const result = await classUUIDToName(uuid);
    setVClass((prev) => { const next = [...prev]; next[index] = result; return next; });
  };

  const fetchParkingZoneName = async (zoneId, index) => {
    try {
      const result = await parkingZoneIdToName(zoneId);
      setParkingZones((prev) => { const next = [...prev]; next[index] = result; return next; });
    } catch (error) {
      console.error('Error fetching parking zone:', error);
    }
  };

  React.useEffect(() => {
    if (regValues.vehicles) {
      regValues.vehicles.forEach((vehicle, index) => {
        fetchClassName(vehicle.inClass, index);
        if (showParkingZone && vehicle.parkingZone) fetchParkingZoneName(vehicle.parkingZone, index);
      });
    }
  }, [isOpen, showParkingZone]);

  // --- new: lock the page behind the sheet ------------------------------------
  React.useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // --- new: scroll cue ---------------------------------------------------------
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 24);
  };

  React.useEffect(() => { onScroll(); }, [isOpen, vClass, parkingZones]);

  const handleConfirm = () => { if (submitting) return; onConfirm?.(); };
  const handleGoBack = () => { if (!submitting) onGoBack?.(); };

  if (!isOpen) return null;

  // label/value row: stacked pair on mobile, unchanged block on sm+
  const Field = ({ label, children }) => (
    <div className="flex items-baseline justify-between gap-4 border-b border-gray-100 py-3 last:border-0 sm:block sm:border-0 sm:py-0">
      <p className="shrink-0 text-[15px] text-gray-500 sm:text-sm sm:font-medium sm:text-gray-600">{label}</p>
      <div className="text-right text-[15px] font-semibold text-gray-800 sm:text-left sm:text-base sm:font-normal">{children}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black opacity-50" onClick={handleGoBack}></div>

      {/* Sheet: full-screen on phones, centered card from sm: up */}
      <div className="relative z-10 flex h-[100dvh] w-full flex-col bg-white sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-lg sm:shadow-lg">
        {/* Sticky header */}
        <div className="shrink-0 border-b border-gray-200 px-5 pb-3 pt-[calc(0.875rem+env(safe-area-inset-top))] sm:border-0 sm:p-6 sm:pb-0 sm:text-center">
          <div className="flex items-start justify-between gap-3">
            <h2 className="pt-2 text-xl font-bold text-gray-800 sm:w-full sm:pt-0 sm:text-center sm:text-2xl">
              {title || 'Confirm Registration Information'}
            </h2>
            <button
              type="button"
              aria-label="Go back"
              onClick={handleGoBack}
              disabled={submitting}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-lg text-gray-700 disabled:opacity-50 sm:hidden"
            >
              ✕
            </button>
          </div>
          <p className="mt-1 text-sm font-medium leading-snug text-red-600 sm:mt-2 sm:text-base">
            Please review your information carefully. This cannot be changed after submission.
          </p>
        </div>

        {/* Scrollable body — the ONLY scroller */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-4 sm:px-6 sm:py-0"
        >
          <div className="pb-1 sm:border-b sm:border-gray-200 sm:pb-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 sm:mb-3 sm:text-lg sm:font-semibold sm:normal-case sm:tracking-normal sm:text-gray-700">
              Personal Information
            </h3>
            <div className="sm:grid sm:grid-cols-2 sm:gap-4">
              <Field label="First Name">{regValues.firstName || 'Not provided'}</Field>
              <Field label="Last Name">{regValues.lastName || 'Not provided'}</Field>
              <Field label="Email"><span className="break-all">{regValues.email || 'Not provided'}</span></Field>
              <Field label="Phone">{regValues.phone || 'Not provided'}</Field>
              <Field label="Address">
                {regValues.address ? `${regValues.address}, ${regValues.city}, ${regValues.state}` : 'Not provided'}
              </Field>
              {regValues.selectedSize && <Field label="T-Shirt Size">{regValues.selectedSize}</Field>}
            </div>
          </div>

          <div className="sm:border-b sm:border-gray-200 sm:pb-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 sm:mb-3 sm:text-lg sm:font-semibold sm:normal-case sm:tracking-normal sm:text-gray-700">
              Vehicle Information
            </h3>
            {regValues.vehicles && regValues.vehicles.length > 0 ? (
              <div className="space-y-3">
                {regValues.vehicles.map((vehicle, index) => (
                  <div key={index} className="rounded-lg border border-gray-100 bg-gray-50 p-3.5 sm:border-0 sm:p-3">
                    {/* Mobile: the car itself is the heading */}
                    <p className="text-[17px] font-bold text-gray-800 sm:hidden">
                      {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Not provided'}
                    </p>
                    <div className="mb-2 mt-2 flex items-center gap-2 text-sm text-gray-600 sm:hidden">
                      {showParkingZone && vehicle.parkingZone && parkingZones[index]?.hexcolor && (
                        <span
                          className="h-3.5 w-3.5 rounded border border-gray-300"
                          style={{ backgroundColor: `#${parkingZones[index].hexcolor}` }}
                        ></span>
                      )}
                      <span>
                        {[
                          showParkingZone && vehicle.parkingZone
                            ? parkingZones[index]?.name || `Zone ${vehicle.parkingZone}`
                            : null,
                          vehicle.inClass ? vClass[index]?.name : null,
                        ].filter(Boolean).join(' · ')}
                      </span>
                    </div>

                    {/* Desktop: unchanged heading + grids */}
                    <h4 className="mb-2 hidden font-medium text-gray-700 sm:block">Vehicle {index + 1}</h4>
                    <div className={`hidden sm:grid ${showParkingZone && vehicle.parkingZone ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} sm:mb-2 sm:gap-2`}>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Vehicle</p>
                        <p className="text-gray-800">
                          {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Not provided'}
                        </p>
                      </div>
                      {vehicle.inClass && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">Class</p>
                          <p className="text-gray-800">{vClass[index]?.name}</p>
                          {vClass[index]?.desciption && (
                            <p className="text-xs text-gray-600">{vClass[index].desciption}</p>
                          )}
                        </div>
                      )}
                      {showParkingZone && vehicle.parkingZone && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">Parking Zone</p>
                          <div className="flex items-center gap-2">
                            {parkingZones[index]?.hexcolor && (
                              <div
                                className="h-4 w-4 rounded border border-gray-300"
                                style={{ backgroundColor: `#${parkingZones[index].hexcolor}` }}
                              ></div>
                            )}
                            <p className="text-gray-800">{parkingZones[index]?.name || `Zone ${vehicle.parkingZone}`}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="sm:grid sm:grid-cols-3 sm:gap-2">
                      <Field label="Engine">{vehicle.engine || 'Not provided'}</Field>
                      <Field label="Club">{vehicle.club || 'None specified'}</Field>
                      <Field label="Special Features">{vehicle.specialfeatures || 'None specified'}</Field>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No vehicles provided</p>
            )}
          </div>
        </div>

        {/* Sticky footer — always reachable */}
        <div className="relative shrink-0 border-t border-gray-200 bg-white px-5 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:rounded-b-lg sm:px-6 sm:pb-6 sm:pt-4">
          {/* fade + scroll cue */}
          <div className="pointer-events-none absolute inset-x-0 -top-10 h-10 bg-gradient-to-t from-white to-transparent sm:hidden"></div>
          {!atBottom && (
            <div className="pointer-events-none absolute -top-[34px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gray-800 px-3 py-1 text-xs font-semibold text-white shadow-lg sm:hidden">
              Scroll for more ↓
            </div>
          )}

          <div className="flex gap-3 sm:justify-between sm:gap-0">
            <button
              onClick={handleGoBack}
              disabled={submitting}
              className="min-h-[52px] flex-1 basis-0 rounded-lg border border-gray-300 bg-white text-[17px] font-semibold text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:min-h-[44px] sm:flex-none sm:rounded sm:border-0 sm:bg-gray-500 sm:px-6 sm:py-2 sm:text-base sm:font-normal sm:text-white sm:hover:bg-gray-600"
            >
              Go Back
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitting}
              className="min-h-[52px] grow-[2] basis-0 rounded-lg bg-blue-700 text-[17px] font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:min-h-[44px] sm:grow-0 sm:rounded sm:bg-blue-500 sm:px-6 sm:py-2 sm:text-base sm:font-normal sm:hover:bg-blue-600"
            >
              {submitting ? 'Submitting…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
